import {sha256} from '../../../global-utils/helper-implementations/sha256';
import {TrackingCustomerData, TrackingWishlist} from '../../../types';
import {TrackingClientStateHolder} from '../helpers/clientState';
import {executeTrackBasketAction} from './trackBasketAction';

export interface CustomerDataEvent {
  event: 'customerData';
  customerId: number | null;
  cidhash: string | null;
  customerType: 'guest' | 'existing' | 'new';
  nkta: number | null;
  vp: boolean;
  gender: 'male' | 'female' | null;
  emh: string | null;
  emhec: string | null;
  basketCount: number;
  wishlistCount: number;
  transactions: number | null;
}

export interface CustomerDataEventGA4 {
  event: 'customer_data';
  customer_id: number | null;
  cidhash: string | null;
  customer_type: 'guest' | 'existing' | 'new';
  login: boolean;
  login_method: 'none' | 'email' | 'facebook' | 'apple';
  nkta: number | null;
  bi_vp: boolean;
  eh: string | null;
  transactions: number | null;
}

export const trackCustomerData =
  ({
    customer,
    basketCount,
    wishlistCount,
    basketItems,
    wishlist,
  }: {
    customer: TrackingCustomerData;
    basketCount: number;
    wishlistCount: number;
    basketItems: {id: number; quantity: number; sizeId: number}[];
    wishlist: TrackingWishlist;
  }) =>
  (clientState: TrackingClientStateHolder) => {
    const {customerDataInitialized, queuedBasketActions} =
      clientState.getCurrent();
    clientState.update({
      wishlist,
      basketItems,
      customerDataInitialized: true,
    });

    const {customerId, cidhash} = getCustomerIdAndCustomerIdHash(customer);

    const eventGA3: CustomerDataEvent = {
      event: 'customerData',
      customerId,
      cidhash,
      customerType: customer.type,
      nkta: customer.type !== 'guest' ? customer.pc180Score : null,
      vp: customer.type === 'existing' ? customer.vip : false,
      gender: customer.type !== 'guest' ? customer.gender : null,
      emh: customer.type !== 'guest' ? customer.emh : null,
      emhec: customer.type !== 'guest' ? customer.emhec : null,
      basketCount,
      wishlistCount,
      transactions: customer.type !== 'guest' ? customer.transactions : null,
    };

    const eventGA4: CustomerDataEventGA4 = {
      event: 'customer_data',
      customer_id: customerId,
      cidhash,
      customer_type: customer.type,
      login: customer.type !== 'guest',
      login_method: 'none',
      nkta: customer.type !== 'guest' ? customer.pc180Score : null,
      bi_vp: customer.type === 'existing' ? customer.vip : false,
      eh: customer.type !== 'guest' ? customer.emh : null,
      transactions: customer.type !== 'guest' ? customer.transactions : null,
    };

    // Execute queued basket actions
    if (customerDataInitialized) {
      queuedBasketActions.map(executeTrackBasketAction(clientState));
      clientState.update({queuedBasketActions: []});
    }

    return {
      GA3: eventGA3,
      GA4: eventGA4,
    };
  };

export const getCustomerIdAndCustomerIdHash = (
  customer: TrackingCustomerData
) => {
  const noCustomerId = {
    customerId: null,
    cidhash: null,
  };

  if (customer.type === 'guest') {
    return noCustomerId;
  } else if (
    (customer.type === 'new' || customer.type === 'existing') &&
    customer.id !== undefined
  ) {
    const cidNumber = Number(customer.id);
    const cidString = customer.id.toString();
    const isCIDNumber = !isNaN(cidNumber);

    return isCIDNumber
      ? {
          customerId: cidNumber,
          cidhash: sha256(cidString) || null,
        }
      : noCustomerId;
  } else {
    // make ts happy
    return noCustomerId;
  }
};
